import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Home, BookOpen, LayoutGrid, Users, Sparkles, Settings as SettingsIcon,
  Shield, Bell, ArrowRight, ChevronDown, LogOut,
} from 'lucide-react';
import { authAPI, boardsAPI } from '../api';
import { getTranslation } from '../i18n';
import { getPeriod, getDailyQuote } from '../data/heroContent';
import PlatformFooter from '../components/PlatformFooter';

export default function WorkspaceDashboard() {
  const navigate = useNavigate();
  document.title = 'Maestra - Workspace';

  const [settings, setSettings] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [period, setPeriod] = useState(() => getPeriod(new Date().getHours()));

  useEffect(() => {
    authAPI.getSettings().then(({ data }) => setSettings(data)).catch(() => {});
    boardsAPI.list()
      .then(({ data }) => setHasUnread((data || []).some((b) => b.has_unread_client_activity)))
      .catch(() => {});
    const id = setInterval(() => setPeriod(getPeriod(new Date().getHours())), 60000);
    return () => clearInterval(id);
  }, []);

  const lang = settings?.language || 'en';
  const t = getTranslation(lang);
  const active = settings?.active_modules || ['boards', 'client_space'];
  const dailyQuote = useMemo(() => getDailyQuote(lang), [lang]);

  const handleLogout = () => {
    Cookies.remove('kahla_token');
    Cookies.remove('kahla_role');
    navigate('/login');
  };

  if (!settings) {
    return <div className="wsd" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><div className="spinner" /></div>;
  }

  const brandName = settings.branding?.workspace_name || t.studio_name || 'Maestra';
  const firstName = (settings.username || '').trim().split(/\s+/)[0] || '';
  const greeting = period === 'morning' ? (t.wd_greet_morning || 'Good morning')
    : period === 'afternoon' ? (t.wd_greet_afternoon || 'Good afternoon')
    : (t.wd_greet_evening || 'Good evening');

  const MODULES = [
    { key: 'library', core: true, path: '/library', icon: BookOpen, name: t.wd_library || 'Library', desc: t.wd_library_desc || 'All your inspirations, images and collections.' },
    { key: 'boards', path: '/boards', icon: LayoutGrid, name: t.wd_boards || 'Boards', desc: t.wd_boards_desc || 'Collaborate with clients and bring ideas to life.' },
    { key: 'client_space', path: '/clients', icon: Users, name: t.wd_clients || 'Clients', desc: t.wd_clients_desc || 'Client cards, history, documents and memories.' },
    { key: 'creator_space', path: '/creator', icon: Sparkles, name: t.wd_creator || 'Creator Space', desc: t.wd_creator_desc || 'Your space for ideas, planning and growth.' },
  ].filter((m) => m.core || active.includes(m.key) || (m.key === 'creator_space' && settings.role === 'admin'));

  const NAV = [
    { key: 'home', icon: Home, label: t.wd_home || 'Home', path: '/dashboard', current: true },
    ...MODULES.filter((m) => !m.soon).map((m) => ({ key: m.key, icon: m.icon, label: m.name, path: m.path })),
    ...MODULES.filter((m) => m.soon).map((m) => ({ key: m.key, icon: m.icon, label: m.name, soon: true })),
  ];

  return (
    <div className="wsd" data-testid="workspace-dashboard">
      {/* ── Sidebar ── */}
      <aside className="wsd-side">
        <div className="wsd-side__logo" data-testid="workspace-maestra-logo">
          <img src="/maestra-logo.png" alt="Maestra" />
          <span className="wsd-side__wordmark">MAESTRA</span>
        </div>
        <p className="wsd-side__brand" data-testid="workspace-brand">{brandName}</p>
        <nav className="wsd-side__nav" data-testid="workspace-sidebar-nav">
          {NAV.map(({ key, icon: Icon, label, path, current, soon }) => (
            <button
              key={key}
              type="button"
              data-testid={`sidenav-${key}`}
              className={`wsd-side__item${current ? ' is-current' : ''}${soon ? ' is-soon' : ''}`}
              onClick={() => { if (path && !current) navigate(path); }}
              disabled={soon}
            >
              <Icon size={16} strokeWidth={1.4} />
              <span>{label}</span>
              {soon && <em className="wsd-soon">{t.wd_soon || 'Soon'}</em>}
            </button>
          ))}
        </nav>
        <span className="wsd-side__rule" aria-hidden="true" />
        {settings.role === 'admin' && (
          <button type="button" className="wsd-side__item" data-testid="workspace-admin-btn" onClick={() => navigate('/admin')}>
            <Shield size={16} strokeWidth={1.4} />
            <span>{t.admin_panel || 'Admin'}</span>
          </button>
        )}
        <button type="button" className="wsd-side__item" data-testid="workspace-settings-btn" onClick={() => navigate('/settings')}>
          <SettingsIcon size={16} strokeWidth={1.4} />
          <span>{t.settings || 'Settings'}</span>
        </button>

        <div className="wsd-side__user">
          {userMenu && (
            <button type="button" className="wsd-side__logout" data-testid="workspace-logout-btn" onClick={handleLogout}>
              <LogOut size={14} strokeWidth={1.4} />
              <span>{t.sign_out || 'Sign out'}</span>
            </button>
          )}
          <button type="button" className="wsd-side__userbtn" data-testid="workspace-user-btn" onClick={() => setUserMenu(!userMenu)}>
            <span className="wsd-side__avatar">{(settings.username || 'M')[0].toUpperCase()}</span>
            <span className="wsd-side__username">{settings.username}</span>
            <ChevronDown size={13} strokeWidth={1.5} style={{ transform: userMenu ? 'rotate(180deg)' : 'none', transition: 'transform 250ms ease' }} />
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="wsd-main">
        <button type="button" className="wsd-bell" data-testid="workspace-bell" onClick={() => navigate('/boards')} title={t.wd_bell || 'Client activity'}>
          <Bell size={17} strokeWidth={1.4} />
          {hasUnread && <span className="wsd-bell__dot" />}
        </button>

        <div className="wsd-hero">
          <h1 className="wsd-hero__greeting" key={greeting} data-testid="workspace-greeting">
            {greeting}, <em>{firstName}.</em>
          </h1>
          <span className="wsd-hero__rule" aria-hidden="true" />
          <p className="wsd-hero__sub">{t.wd_workspace_sub || 'This is your creative workspace'}</p>
        </div>

        <div className="wsd-cards" data-testid="workspace-modules">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                type="button"
                data-testid={`module-entry-${m.key}`}
                className={`wsd-card${m.soon ? ' is-soon' : ''}`}
                style={{ animationDelay: `${260 + i * 110}ms` }}
                onClick={() => { if (!m.soon) navigate(m.path); }}
                disabled={m.soon}
              >
                {m.soon && <span className="wsd-card__soon">{t.wd_soon || 'Soon'}</span>}
                <span className="wsd-card__icon"><Icon size={30} strokeWidth={1.1} /></span>
                <span className="wsd-card__title">{m.name}</span>
                <span className="wsd-card__hair" aria-hidden="true" />
                <span className="wsd-card__desc">{m.desc}</span>
                <span className="wsd-card__arrow"><ArrowRight size={16} strokeWidth={1.3} /></span>
              </button>
            );
          })}
        </div>

        <figure className="wsd-quote" data-testid="daily-quote">
          <span className="wsd-quote__mark">&ldquo;</span>
          <blockquote>{dailyQuote}</blockquote>
          <span className="wsd-quote__hair" aria-hidden="true" />
        </figure>

        <footer className="wsd-footer" data-testid="workspace-footer">
          <img src="/maestra-logo.png" alt="" aria-hidden="true" className="wsd-footer__mark" />
          <div>
            <PlatformFooter />
          </div>
        </footer>
      </main>
    </div>
  );
}
